import React, { useCallback } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTranslationKeys } from 'intl/keys';
import {
  Box,
  TextField,
  IconButton,
  Button,
  FormControlLabel,
  FormHelperText,
  Switch,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useIntl } from 'react-intl';
import { MemoAnswerCardHeader } from './AnswerCardHeader';
import { AnswerPanelProps, OptionSelectionAnswerConfig } from './types';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { createUniqueId } from 'shared/utils/id/id';
import { RichTextEditor } from 'components/common/RichTextEditor';
import { STANDARD_TITLE_MAXIMUM } from 'helpers/validators';
import {
  OPTION_ROW_SX,
  OPTION_LABEL_FIELD_SX,
  OPTION_DELETE_SLOT_SX,
  OPTION_LIST_SX,
  OPTION_SELECTION_FIELDS_ROW_SX,
  DELETE_ANSWER_BUTTON_SX,
} from './styles';
import { getAddOptionButtonSx, getAnswerCardSx } from './answerTypeTheme';
import { formatFieldError } from './formatFieldError';

export function OptionSelectionAnswerPanel({
  answerId,
  answer,
  fieldErrors,
  onUpdate,
  onDelete,
}: AnswerPanelProps<OptionSelectionAnswerConfig>): React.ReactElement {
  const intl = useIntl();

  const handleUpdate = useCallback(
    (updates: Partial<OptionSelectionAnswerConfig>) => {
      onUpdate(answerId, updates);
    },
    [answerId, onUpdate]
  );

  const handleDelete = useCallback(() => {
    onDelete(answerId);
  }, [answerId, onDelete]);

  const handleLabelChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleUpdate({ label: event.target.value });
    },
    [handleUpdate]
  );

  const handleRequiredChange = useCallback(
    (isRequired: boolean) => {
      handleUpdate({ isRequired });
    },
    [handleUpdate]
  );

  const handleMultiselectChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleUpdate({ isMultiselect: event.target.checked });
    },
    [handleUpdate]
  );

  const handleOptionLabelChange = useCallback(
    (optionId: string, label: string) => {
      const updatedOptions = answer.options.map((opt) =>
        opt._id === optionId ? { ...opt, label } : opt
      );
      handleUpdate({ options: updatedOptions });
    },
    [answer.options, handleUpdate]
  );

  const handleAddOption = useCallback(() => {
    const newOption = { _id: createUniqueId(), label: '' };
    handleUpdate({ options: [...answer.options, newOption] });
  }, [answer.options, handleUpdate]);

  const handleDeleteOption = useCallback(
    (optionId: string) => {
      if (answer.options.length <= 1) return;
      const updatedOptions = answer.options.filter((opt) => opt._id !== optionId);
      handleUpdate({ options: updatedOptions });
    },
    [answer.options, handleUpdate]
  );

  return (
    <Box sx={getAnswerCardSx(answer.type)}>
      <MemoAnswerCardHeader
        answerType={answer.type}
        isRequired={answer.isRequired}
        onRequiredChange={handleRequiredChange}
        onDelete={handleDelete}
      />

      <Box sx={OPTION_SELECTION_FIELDS_ROW_SX}>
        <TextField
          label={formatTranslation(intl, InspectionTranslationKeys.label)}
          value={answer.label}
          onChange={handleLabelChange}
          size="medium"
          fullWidth
          required
          error={Boolean(fieldErrors?.label)}
          helperText={formatFieldError(intl, fieldErrors?.label)}
        />

        <FormControlLabel
          control={
            <Switch
              checked={answer.isMultiselect}
              onChange={handleMultiselectChange}
              size="small"
            />
          }
          label={formatTranslation(intl, InspectionTranslationKeys.multiselect)}
        />
      </Box>

      <Box sx={OPTION_LIST_SX}>
        {answer.options.map((option, index) => {
          const optionError = formatFieldError(intl, fieldErrors?.options?.[option._id]);
          return (
            <Box key={option._id} sx={OPTION_ROW_SX}>
              {/* One line of rich text with a colour (PT-7250): the backend stores the label as inline HTML. */}
              <Box sx={OPTION_LABEL_FIELD_SX}>
                <RichTextEditor
                  variant='inline'
                  value={option.label}
                  onChange={(label) => handleOptionLabelChange(option._id, label)}
                  label={`${formatTranslation(intl, InspectionTranslationKeys.option)} ${index + 1}`}
                  required
                  dense
                  minRows={1}
                  autoGrow
                  maxLength={STANDARD_TITLE_MAXIMUM}
                  error={Boolean(optionError)}
                  dataQa={`option-label-${option._id}`}
                />
                {optionError && <FormHelperText error>{optionError}</FormHelperText>}
              </Box>
              <Box sx={OPTION_DELETE_SLOT_SX}>
                {answer.options.length > 1 && (
                  <IconButton
                    onClick={() => handleDeleteOption(option._id)}
                    size="small"
                    sx={DELETE_ANSWER_BUTTON_SX}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button
        startIcon={<AddIcon />}
        onClick={handleAddOption}
        size="small"
        sx={getAddOptionButtonSx(AnswerType.OptionSelection)}
      >
        {formatTranslation(intl, InspectionTranslationKeys.addOption)}
      </Button>
    </Box>
  );
}

export const MemoOptionSelectionAnswerPanel = React.memo(OptionSelectionAnswerPanel);
